import { useScroll } from 'react-use';
import { useWindowSize } from 'usehooks-ts';
import {
  useRef,
  useMemo,
  type JSX,
  ReactNode,
  cloneElement,
  PropsWithChildren,
} from 'react';

import { ButtonColors } from 'types';
import { Loader } from 'components/loader';
import { Button } from 'components/button';
import { useStateRef } from 'hooks/useStateRef';
import { Pagination } from 'components/pagination';
import { Placeholder } from 'components/placeholder';
import { RetryRequest } from 'components/retry-request';

import * as Styles from './styles';
import { ListProps } from './types';
import { HorizontalScrollWrapper } from './styles';

const List = ({
  id,
  gap,
  error,
  style,
  testId,
  header,
  footer,
  isError,
  refetch,
  hasMore,
  children,
  count = 0,
  className,
  isLoading,
  topHeader,
  onShowMore,
  searchQuery,
  currentPage,
  hasMinHeight,
  isLoadingMore,
  loadMoreError,
  pageSize = 20,
  setCurrentPage,
  setSearchQuery,
  listWrapperRef,
  dataPlaceholder,
  searchInputProps,
  horizontalScroll,
  mainWrapperStyles,
  searchBarLeftSlot,
  searchPlaceholder,
  searchBarRightSlot,
  searchContainerStyle,
  forceMobileView = false,
  showFooterInEmptyList = false,
}: PropsWithChildren<ListProps>): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [list, setListRef] = useStateRef();
  const size = useWindowSize();

  const listRef = useMemo(() => {
    return { current: list };
  }, [list]);

  const scrollHeight = listRef?.current?.scrollHeight ?? 0;
  const clientHeight = listRef?.current?.clientHeight ?? 0;
  const hasScrollbar = useMemo(() => {
    return scrollHeight > clientHeight;
  }, [size, scrollHeight, scrollHeight]);
  // The rows sit inside the scroll area, so the header must give up exactly
  // the scrollbar's real width (0 with overlay scrollbars, e.g. macOS), not
  // a fixed 8 px, or every header column drifts from its rows.
  const scrollbarWidth = useMemo(() => {
    const element = listRef?.current;
    return hasScrollbar && element
      ? Math.max(element.offsetWidth - element.clientWidth, 0)
      : 0;
  }, [size, hasScrollbar, listRef]);

  const { y } = useScroll(listRef);

  const showMoreButton = useMemo(
    () =>
      hasMore ? (
        <Styles.ShowMoreContainer>
          {loadMoreError && <span role="alert">{loadMoreError}</span>}
          <Button
            transparent
            padding="0"
            onClick={onShowMore}
            disabled={isLoadingMore}
            colorType={ButtonColors.LightBlue}
            text={isLoadingMore ? 'Loading…' : 'Show more'}
          />
        </Styles.ShowMoreContainer>
      ) : null,
    [hasMore, onShowMore, isLoadingMore, loadMoreError]
  );

  const resultHeader = (
    <>
      {header &&
        cloneElement(header, {
          hasScrollbar,
          scrollbarWidth,
          isScrolled: !isLoading && !isError && count > 0 && y > 0,
        })}
      <span id="table-top" />
    </>
  );

  const withHeader = (element: ReactNode) => (
    <>
      {resultHeader}
      {element}
    </>
  );

  const getMainElement = () => {
    if (isLoading) {
      return withHeader(<Loader isLoading isFullScreen={false} />);
    } else if (isError) {
      return withHeader(<RetryRequest error={error} refetch={refetch} />);
    } else if (count > 0) {
      const mainElement = (
        <Styles.Main
          gap={gap}
          ref={setListRef}
          style={mainWrapperStyles}
          horizontalScroll={horizontalScroll}
          horizontalScrollWidth={wrapperRef.current?.scrollWidth}
        >
          {children}
        </Styles.Main>
      );

      const pagination = currentPage && setCurrentPage && (
        <Styles.PaginationContainer
          forceMobileView={forceMobileView}
          horizontalScroll={horizontalScroll}
        >
          <Pagination
            totalCount={count}
            pageSize={pageSize}
            currentPage={currentPage}
            forceMobileView={forceMobileView}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </Styles.PaginationContainer>
      );

      return horizontalScroll ? (
        <>
          <HorizontalScrollWrapper ref={wrapperRef}>
            {withHeader(mainElement)}
          </HorizontalScrollWrapper>
          {showMoreButton}
          {pagination}
          {footer}
        </>
      ) : (
        withHeader(
          <>
            {mainElement}
            {showMoreButton}
            {pagination}
            {footer}
          </>
        )
      );
    } else if (searchQuery) {
      return withHeader(
        searchPlaceholder || <Placeholder text="There are no results" />
      );
    }

    return withHeader(
      <>
        {dataPlaceholder === undefined ? (
          <Placeholder text="You have no data yet" />
        ) : (
          dataPlaceholder
        )}
        {showFooterInEmptyList && footer}
      </>
    );
  };

  return (
    <Styles.ListWrapper
      id={id}
      style={style}
      data-testid={testId}
      ref={listWrapperRef}
      className={className}
      $hasMinHeight={hasMinHeight}
    >
      {topHeader}
      {setSearchQuery && (
        <Styles.SearchContainer style={searchContainerStyle}>
          {searchBarLeftSlot}
          <Styles.SearchInput
            search
            type="text"
            name="search"
            value={searchQuery}
            placeholder="Search"
            onRightIconClick={() => setSearchQuery('')}
            onChange={(e) => setSearchQuery(e.target.value)}
            {...searchInputProps}
          />
          {searchBarRightSlot}
        </Styles.SearchContainer>
      )}
      {getMainElement()}
    </Styles.ListWrapper>
  );
};

export { List };
export type { ListProps };
